import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'

import isEqual from 'lodash.isequal'

import { checkDotNet, getHost } from './utilities'
import { PanelMessageOut } from './types'

declare global {
  interface Window {
    sketchup?: any
    chrome?: any
  }
}

export const useGetHbjson = () => {
  const host: string = getHost()

  const [hbjson, setHbjson] = useState<Blob | undefined>(undefined)
  const hbjsonRef = useRef<Uint8Array | undefined>(undefined)

  const setReturnValue = useCallback((res: PanelMessageOut, force: boolean) => {
    const bytes = new TextEncoder().encode(res.data)

    if (hbjsonRef.current) {
      if (force || !isEqual(bytes, hbjsonRef.current)) {
        setHbjson(
          new Blob([bytes], {
            type: 'application/json;charset=utf-8',
          }),
        )
      }
    } else {
      setHbjson(
        new Blob([bytes], {
          type: 'application/json;charset=utf-8',
        }),
      )
    }
    hbjsonRef.current = bytes
  }, [])

  useEffect(() => {
    
    // @ts-ignore
    window.parent.readHBJSON = (message: PanelMessageOut) => {
      if (typeof message.data !== 'string') throw new Error('readHBJSON did not receive a valid string object.')

      setReturnValue(message, false)
    }
    
    // @ts-ignore
    return () => window.parent.readHbjson = undefined
  }, [setReturnValue])

  const dotNetGetHbjson = useCallback((key = 'use-get-hbjson', 
    selection = false, force = false, useSelection: boolean = false): void => {
    if (!checkDotNet()) return

    const res = window.parent.chrome.webview
      .hostObjects.sync.hbjson.Read(
        key,
        JSON.stringify({
          select: selection,
          useSelection: useSelection
        })
      )

    if (res) {
      setReturnValue(res, force)
    }
  }, [setReturnValue])

  const revitGetHbjson = useCallback((): void => {
    if (!checkDotNet()) return

    window.parent.chrome.webview.hostObjects.sync.hbjson.GetAsync()
  }, [])

  const getHbjson = useMemo(() => {
    switch (host) {
      case 'rhino':
        return dotNetGetHbjson
      case 'revit':
        return revitGetHbjson
      case 'sketchup':
        return dotNetGetHbjson
      default:
        return undefined
    }
  }, [dotNetGetHbjson, host, revitGetHbjson])

  return {
    host,
    hbjson,
    getHbjson,
  }
}
